import {
  UploadFileInfo,
  UploadOnAddEvent,
  UploadOnProgressEvent,
  UploadOnRemoveEvent,
  UploadOnStatusChangeEvent,
} from '@progress/kendo-react-upload';
import { produce } from 'immer';
import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getErrorMessage,
  hasValidationErrors,
  setValidationErrors,
  showErrorToast,
  showSuccessToast,
} from '../../../../../../common/global/helpers';
import Button from '../../../../../../common/widgets/Button';
import FileInput from '../../../../../../common/widgets/FileInput';
import Icons from '../../../../../../common/widgets/Icons';
import {
  Notification,
  NotificationType,
} from '../../../../../../common/widgets/Notification';
import { config } from '../../../../config';
import { wait } from '../../../../helpers';
import { ErrorState, ErrorType } from '../../../../models/Errors';
import { resources } from '../../../../resources/resources';
import { fileImportService } from '../../../../services/file-import';
import { parseRowErrorLines } from '../../../BenefitsPage/utils';
import {
  onRemoveRequest,
  onSaveRequest,
} from '../../EmployeesAdmin/FileImportPage/helper';

interface ImportOrdersFileFormState {
  isDownlodingTemplate: boolean;
  isSaving: boolean;
  files: UploadFileInfo[];
}

export default function ImportOrdersFileForm() {
  const { t: translate } = useTranslation();
  const defaultState: ImportOrdersFileFormState = {
    isDownlodingTemplate: false,
    isSaving: false,
    files: [] as UploadFileInfo[],
  };
  const [state, setState] = useState<ImportOrdersFileFormState>(defaultState);
  const [errors, setErrors] = useState<ErrorState>({});
  const [rowErrors, setRowErrors] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const clearErrors = (field: keyof typeof errors) => {
    setErrors(
      produce(d => {
        d[field] = [];
      }),
    );
  };

  const onAdd = (event: UploadOnAddEvent) => {
    setState(
      produce(d => {
        d.files = event.newState;
      }),
    );
    clearErrors('file');
    setShowSuccess(false);
  };

  const onRemove = (event: UploadOnRemoveEvent) => {
    setState(
      produce(d => {
        d.files = event.newState;
      }),
    );
    clearErrors('file');
    setShowSuccess(false);
  };

  const onProgress = (event: UploadOnProgressEvent) => {
    setState(
      produce(d => {
        d.files = event.newState;
      }),
    );
  };

  const onStatusChange = (event: UploadOnStatusChangeEvent) => {
    setState(
      produce(d => {
        d.files = event.newState;
      }),
    );
    clearErrors('file');
    setShowSuccess(false);
  };

  const handleDownloadEmployeeExample = async () => {
    setState(
      produce(d => {
        d.isDownlodingTemplate = true;
      }),
    );
    const response = await fileImportService.downloadOrderTemplate();
    if (response.hasErrors) {
      showErrorToast(
        translate(resources.admin.fileImport.downloadTemplateError),
      );
    }
    setState(
      produce(d => {
        d.isDownlodingTemplate = false;
      }),
    );
  };

  const handleSubmit = async () => {
    if (!state.files[0].getRawFile) {
      return;
    }

    setState(
      produce(d => {
        d.isSaving = true;
      }),
    );

    setRowErrors(null);
    setShowSuccess(false);

    const formData = new FormData();
    formData.append('file', state.files[0].getRawFile(), state.files[0].name);
    const response = await fileImportService.importOrders(formData);

    if (!response.hasErrors) {
      if (response.data.rowErrors) {
        setRowErrors(response.data.rowErrors);
      } else {
        setShowSuccess(true);
        showSuccessToast(
          translate(resources.admin.fileImport.budgetTab.importSuccess),
        );
      }

      const reportFileName = `Import orders report [${moment().format(
        config.dateFormat,
      )}]`;

      await wait(1000);
      await fileImportService.downloadOrdersReport(
        response.data.code,
        reportFileName,
        'xlsx',
      );

      setState(defaultState);
    } else if (response.errorType === ErrorType.ValidationError) {
      setValidationErrors(response, setErrors);
    } else {
      showErrorToast(translate(resources.admin.fileImport.importError));
    }
    setState(
      produce(d => {
        d.isSaving = false;
      }),
    );
  };

  return (
    <>
      {showSuccess && !rowErrors && !hasValidationErrors(errors, 'null') && (
        <Notification
          type={NotificationType.Success}
          messages={[
            {
              message: translate(
                resources.admin.import.excel.validation.processedSuccessfully,
              ),
              onShowDetailsClick: undefined,
            },
          ]}
          showClose={true}
        />
      )}
      {rowErrors && (
        <Notification
          type={NotificationType.Warning}
          messages={[
            {
              message: translate(
                resources.admin.import.excel.validation.rowsError,
                {
                  0: parseRowErrorLines(rowErrors),
                },
              ),
              onShowDetailsClick: undefined,
            },
          ]}
          showClose={true}
        />
      )}
      {hasValidationErrors(errors, 'null') && (
        <Notification
          type={NotificationType.Warning}
          messages={errors.null.map(e => ({
            message: getErrorMessage(e, translate),
            onShowDetailsClick: undefined,
          }))}
          showClose={true}
        />
      )}
      <div className="upload-steps">
        <div className="upload-step">
          <h2>{translate(resources.admin.fileImport.stepOneTitle)}</h2>
          <p>{translate(resources.admin.importOrders.stepOneDescription)}</p>
          <div className="upload-actions">
            <Button
              className="btn btn-solid btn-sm"
              onClick={handleDownloadEmployeeExample}
              loading={state.isDownlodingTemplate}
              icon={<Icons.DownloadFile />}
            >
              <span className="btn-text">
                {translate(
                  resources.admin.fileImport.stepOneActions.downloadTemplate,
                )}
              </span>
            </Button>
          </div>
        </div>
        <div className="upload-step">
          <h2>{translate(resources.admin.fileImport.stepTwoTitle)}</h2>
          <p>{translate(resources.admin.importOrders.stepTwoDescription)}</p>
          <div className="admin-upload">
            <FileInput
              batch={false}
              multiple={false}
              files={state.files}
              restrictions={{
                allowedExtensions: ['.xlsx'],
                maxFileSize: 15000000,
              }}
              onAdd={onAdd}
              onRemove={onRemove}
              onProgress={onProgress}
              onStatusChange={onStatusChange}
              withCredentials={false}
              saveUrl={onSaveRequest}
              removeUrl={onRemoveRequest}
              errors={errors.file}
            />
          </div>
        </div>
        <div className="form-actions left-actions">
          <Button
            className="btn btn-solid"
            onClick={handleSubmit}
            loading={state.isSaving}
          >
            <span className="btn-text">
              {translate(resources.admin.action.upload)}
            </span>
          </Button>
        </div>
      </div>
    </>
  );
}
